<template>
  <div class="slide-in-view" :style="containerStyle">
    <slot />
  </div>
</template>

<script>
export default {
  name: 'SlideInView',
  props: {
    /**
     * The visibility threshold at which
     * the component is considered "in view."
     * 0 = any part is visible, 1 = all must be visible.
     */
    threshold: {
      type: Number,
      default: 0.1,
    },
    /**
     * Distance to slide from, e.g. '100%', '50px', etc.
     * If direction = 'up', it slides from 100% below its final position.
     */
    slideDistance: {
      type: String,
      default: '100%',
    },
    /**
     * Direction of the slide: 'up', 'down', 'left', 'right'
     */
    direction: {
      type: String,
      default: 'up',
      validator: (val) => ['up', 'down', 'left', 'right'].includes(val),
    },
    /**
     * If true, the component will animate again
     * if it scrolls out and back into view.
     */
    repeat: {
      type: Boolean,
      default: false,
    },
    /**
     * Animation duration in seconds
     */
    duration: {
      type: Number,
      default: 0.5,
    },
  },
  data() {
    return {
      isInView: false, // Whether the component has been detected in view
      observer: null, // Intersection Observer instance
    }
  },
  computed: {
    /**
     * Dynamically compute the transform style
     * based on direction and in-view state.
     */
    containerStyle() {
      // Determine the initial transform when NOT in view
      let initialTransform
      switch (this.direction) {
        case 'up':
          initialTransform = `translateY(${this.slideDistance})`
          break
        case 'down':
          initialTransform = `translateY(-${this.slideDistance})`
          break
        case 'left':
          initialTransform = `translateX(${this.slideDistance})`
          break
        case 'right':
          initialTransform = `translateX(-${this.slideDistance})`
          break
      }

      // If it's in view, transform is 'none' (so it ends in normal position)
      const transform = this.isInView ? 'none' : initialTransform

      return {
        transform,
        opacity: this.isInView ? '1' : '0',
        transition: `transform ${this.duration}s ease, opacity ${this.duration}s ease`,
      }
    },
  },
  mounted() {
    // Create an Intersection Observer with the user-defined threshold
    const options = {
      threshold: this.threshold,
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.isInView = true
          // If the user doesn't want repeated animations, unobserve immediately
          if (!this.repeat) {
            this.observer.unobserve(entry.target)
          }
        } else if (this.repeat) {
          // If repeat is true, we reset the animation when scrolling out of view
          this.isInView = false
        }
      })
    }, options)

    // Observe this component's root element
    this.observer.observe(this.$el)
  },
  beforeDestroy() {
    // Clean up the observer if needed
    if (this.observer) {
      this.observer.disconnect()
    }
  },
}
</script>

<style scoped>
.slide-in-view {
  /* You can override these or keep them as a baseline:
       - We'll rely on inline styles from containerStyle 
         to handle transform & transition. */
}
</style>
